import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { usePermisos } from '../../../ganchos/usePermisos'
import {
  CogIcon,
  ShieldCheckIcon,
  BellIcon,
  EnvelopeIcon,
  CircleStackIcon,
  KeyIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import { 
  configuracionService,
  ConfiguracionGeneral as ConfiguracionGeneralAPI,
  ConfiguracionSeguridad as ConfiguracionSeguridadAPI,
  ConfiguracionEmail as ConfiguracionEmailAPI,
  ConfiguracionAlmacenamiento as ConfiguracionAlmacenamientoAPI,
  ConfiguracionNotificaciones as ConfiguracionNotificacionesAPI,
  ParametroPersonalizado as ParametroPersonalizadoAPI
} from '../../../services/configuracionService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'

// Interfaces extendidas del servicio
interface ConfiguracionGeneral extends ConfiguracionGeneralAPI {
  logoUrl?: string
  monedaPredeterminada?: string
}

interface ConfiguracionSeguridad extends ConfiguracionSeguridadAPI {
  ipsBloqueadas?: string[]
  dominiosPermitidos?: string[]
}

interface ConfiguracionEmail extends ConfiguracionEmailAPI {
  plantillasBienvenida?: boolean
  plantillasRecuperacion?: boolean
  notificacionesAdmin?: boolean
}

interface ConfiguracionAlmacenamiento extends ConfiguracionAlmacenamientoAPI {
  limiteTotalUsuario?: number
  frecuenciaRespaldo?: 'diario' | 'semanal' | 'mensual'
  retencionRespaldos?: number
}

interface ConfiguracionNotificaciones extends ConfiguracionNotificacionesAPI {
  notificacionesSMS?: boolean
  frecuenciaDigest?: 'inmediato' | 'diario' | 'semanal'
  tiposNotificacion?: {
    nuevoUsuario: boolean
    nuevaDescarga: boolean
    errorSistema: boolean
    mantenimiento: boolean
    actualizacion: boolean
  }
}

interface ParametroPersonalizado extends ParametroPersonalizadoAPI {
  esEditable?: boolean
}

const PaginaConfiguracionSistema: React.FC = () => {
  const { seccion } = useParams<{ seccion: string }>()
  const navigate = useNavigate()
  const { tienePermiso } = usePermisos()
  const { showToast } = useToast()
  
  const [configuracionGeneral, setConfiguracionGeneral] = useState<ConfiguracionGeneral>({
    nombreSistema: 'LogiXPort',
    descripcionSistema: 'Sistema de gestión logística y transporte',
    idiomaDefecto: 'es',
    zonaHoraria: 'America/Mexico_City',
    formatoFecha: 'DD/MM/YYYY',
    modoMantenimiento: false,
    registroPublico: true,
    verificacionEmail: false,
    logoUrl: '/logo.png',
    monedaPredeterminada: 'MXN'
  })

  const [configuracionSeguridad, setConfiguracionSeguridad] = useState<ConfiguracionSeguridad>({
    longitudMinimaPassword: 8,
    requiereCaracteresEspeciales: true,
    requiereNumeros: true,
    requiereMayusculas: true,
    expiracionSesion: 24,
    intentosMaximosLogin: 5,
    tiempoBloqueo: 30,
    autenticacionDosFactor: false,
    ipsBloqueadas: [],
    dominiosPermitidos: ['@empresa.com', '@logixport.com']
  })

  const [configuracionEmail, setConfiguracionEmail] = useState<ConfiguracionEmail>({
    servidorSMTP: 'smtp.gmail.com',
    puertoSMTP: 587,
    usuarioSMTP: '',
    passwordSMTP: '',
    encriptacion: 'tls',
    emailRemitente: 'noreply@logixport.com',
    nombreRemitente: 'LogiXPort',
    plantillaBienvenida: '',
    plantillaRecuperacion: '',
    notificacionesEmail: true,
    plantillasBienvenida: true,
    plantillasRecuperacion: true,
    notificacionesAdmin: true
  })

  const [configuracionAlmacenamiento, setConfiguracionAlmacenamiento] = useState<ConfiguracionAlmacenamiento>({
    tipoAlmacenamiento: 'local',
    tamañoMaximoArchivo: 100,
    tiposArchivosPermitidos: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'png', 'gif'],
    compresionAutomatica: true,
    respaldoAutomatico: true,
    limiteTotalUsuario: 1000,
    frecuenciaRespaldo: 'diario',
    retencionRespaldos: 30
  })

  const [configuracionNotificaciones, setConfiguracionNotificaciones] = useState<ConfiguracionNotificaciones>({
    notificacionesEmail: true,
    notificacionesPush: false,
    notificacionesSlack: false,
    notificacionesDiscord: false,
    frecuenciaReportes: 'diario',
    horaEnvioReportes: '09:00',
    notificacionesSMS: false,
    frecuenciaDigest: 'diario',
    tiposNotificacion: {
      nuevoUsuario: true,
      nuevaDescarga: true,
      errorSistema: true,
      mantenimiento: true,
      actualizacion: true
    }
  })

  const [parametrosPersonalizados, setParametrosPersonalizados] = useState<ParametroPersonalizado[]>([])

  const [seccionActiva, setSeccionActiva] = useState<'general' | 'seguridad' | 'email' | 'almacenamiento' | 'notificaciones' | 'personalizado'>('general')
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [modalParametro, setModalParametro] = useState<{ isOpen: boolean; parametro?: ParametroPersonalizado; tipo: 'crear' | 'editar' }>({
    isOpen: false,
    tipo: 'crear'
  })
  const [nuevoParametro, setNuevoParametro] = useState<Partial<ParametroPersonalizado>>({
    clave: '',
    valor: '',
    tipo: 'string',
    descripcion: '',
    categoria: '',
    esEditable: true
  })

  // Sincronizar URL con sección activa
  useEffect(() => {
    if (seccion) {
      if (seccion === 'respaldos') {
        setSeccionActiva('almacenamiento')
      } else if (['general', 'seguridad', 'email', 'almacenamiento', 'notificaciones', 'personalizado'].includes(seccion)) {
        setSeccionActiva(seccion as any)
      }
    } else {
      setSeccionActiva('general')
    }
  }, [seccion])

  // Cargar configuraciones
  useEffect(() => {
    const cargarConfiguraciones = async () => {
      setCargando(true)
      try {
        // Cargar configuración general
        const configGeneral = await configuracionService.obtenerConfiguracionGeneral()
        setConfiguracionGeneral(prev => ({ ...prev, ...configGeneral }))
        
        // Cargar configuración de seguridad
        const configSeguridad = await configuracionService.obtenerConfiguracionSeguridad()
        setConfiguracionSeguridad(prev => ({ ...prev, ...configSeguridad }))
        
        // Cargar configuración de email
        const configEmail = await configuracionService.obtenerConfiguracionEmail()
        setConfiguracionEmail(prev => ({ ...prev, ...configEmail }))
        
        // Cargar configuración de almacenamiento
        const configAlmacenamiento = await configuracionService.obtenerConfiguracionAlmacenamiento()
        setConfiguracionAlmacenamiento(prev => ({ ...prev, ...configAlmacenamiento }))
        
        // Cargar configuración de notificaciones
        const configNotificaciones = await configuracionService.obtenerConfiguracionNotificaciones()
        setConfiguracionNotificaciones(prev => ({ ...prev, ...configNotificaciones }))
        
        // Cargar parámetros personalizados
        const parametros = await configuracionService.obtenerParametrosPersonalizados()
        setParametrosPersonalizados(parametros.map(param => ({ ...param, esEditable: true })))
      } catch (error) {
        console.error('Error al cargar configuraciones:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible cargar la configuración del sistema.',
          titulosPorCodigo: {
            forbidden: 'Acceso denegado',
            unauthorized: 'Sesión no válida',
          },
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        // En caso de error, mantener los valores por defecto
      } finally {
        setCargando(false)
      }
    }

    cargarConfiguraciones()
  }, [])

  const guardarConfiguracion = async (seccion: string) => {
    setGuardando(true)
    try {
      switch (seccion) {
        case 'general':
          const configGeneralActualizada = await configuracionService.actualizarConfiguracionGeneral(configuracionGeneral)
          setConfiguracionGeneral(prev => ({ ...prev, ...configGeneralActualizada }))
          break
        case 'seguridad':
          const configSeguridadActualizada = await configuracionService.actualizarConfiguracionSeguridad(configuracionSeguridad)
          setConfiguracionSeguridad(prev => ({ ...prev, ...configSeguridadActualizada }))
          break
        case 'email':
          const configEmailActualizada = await configuracionService.actualizarConfiguracionEmail(configuracionEmail)
          setConfiguracionEmail(prev => ({ ...prev, ...configEmailActualizada }))
          break
        case 'almacenamiento':
          const configAlmacenamientoActualizada = await configuracionService.actualizarConfiguracionAlmacenamiento(configuracionAlmacenamiento)
          setConfiguracionAlmacenamiento(prev => ({ ...prev, ...configAlmacenamientoActualizada }))
          break
        case 'notificaciones':
          const configNotificacionesActualizada = await configuracionService.actualizarConfiguracionNotificaciones(configuracionNotificaciones)
          setConfiguracionNotificaciones(prev => ({ ...prev, ...configNotificacionesActualizada }))
          break
      }
      console.log(`Configuración de ${seccion} guardada exitosamente`)
      showToast({
        type: 'success',
        title: 'Cambios guardados',
        message: `La configuración de ${seccion} se actualizó correctamente.`,
      })
    } catch (error) {
      console.error(`Error al guardar configuración de ${seccion}:`, error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: `No fue posible guardar la configuración de ${seccion}.`,
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          not_found: 'Configuración no encontrada',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica los valores capturados antes de guardar la configuración.',
          forbidden: 'No tienes permisos para modificar la configuración del sistema.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    } finally {
      setGuardando(false)
    }
  }

  const probarConexionEmail = async () => {
    setCargando(true)
    try {
      const resultado = await configuracionService.probarConexionEmail()
      if (resultado.exito) {
        showToast({
          type: 'success',
          title: 'Conexión exitosa',
          message: 'La conexión SMTP se validó correctamente.',
        })
      } else {
        showToast({
          type: 'error',
          title: 'Conexión fallida',
          message: resultado.mensaje,
        })
      }
    } catch (error) {
      console.error('Error al probar conexión de email:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible probar la conexión SMTP.',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
        },
        mensajesPorCodigo: {
          validation_error: 'Revisa servidor, puerto y credenciales SMTP antes de probar la conexión.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    } finally {
      setCargando(false)
    }
  }

  const agregarParametroPersonalizado = async () => {
    if (nuevoParametro.clave && nuevoParametro.valor) {
      try {
        const parametroCreado = await configuracionService.crearParametroPersonalizado({
          clave: nuevoParametro.clave,
          valor: nuevoParametro.valor,
          tipo: nuevoParametro.tipo || 'string',
          descripcion: nuevoParametro.descripcion || '',
          categoria: nuevoParametro.categoria || 'general'
        })
        setParametrosPersonalizados(prev => [...prev, { ...parametroCreado, esEditable: true }])
        setNuevoParametro({ clave: '', valor: '', tipo: 'string', descripcion: '', categoria: '', esEditable: true })
        setModalParametro({ isOpen: false, tipo: 'crear' })
        showToast({
          type: 'success',
          title: 'Parámetro creado',
          message: 'El parámetro personalizado se creó correctamente.',
        })
      } catch (error) {
        console.error('Error al crear parámetro personalizado:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible crear el parámetro personalizado.',
          titulosPorCodigo: {
            validation_error: 'Datos inválidos',
            forbidden: 'Acceso denegado',
            conflict: 'Parámetro duplicado',
          },
          mensajesPorCodigo: {
            validation_error: 'Verifica la clave, el valor y la categoría del parámetro.',
            forbidden: 'No tienes permisos para crear parámetros personalizados.',
          },
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
      }
    }
  }

  const editarParametroPersonalizado = (parametro: ParametroPersonalizado) => {
    setNuevoParametro(parametro)
    setModalParametro({ isOpen: true, parametro, tipo: 'editar' })
  }

  const guardarParametroEditado = async () => {
    if (modalParametro.parametro && nuevoParametro.clave && nuevoParametro.valor) {
      try {
        const parametroActualizado = await configuracionService.actualizarParametroPersonalizado(
          modalParametro.parametro.id,
          {
            clave: nuevoParametro.clave,
            valor: nuevoParametro.valor,
            tipo: nuevoParametro.tipo || 'string',
            descripcion: nuevoParametro.descripcion || '',
            categoria: nuevoParametro.categoria || 'general'
          }
        )
        setParametrosPersonalizados(prev => 
          prev.map(p => 
            p.id === modalParametro.parametro!.id 
              ? { ...parametroActualizado, esEditable: true }
              : p
          )
        )
        setNuevoParametro({ clave: '', valor: '', tipo: 'string', descripcion: '', categoria: '', esEditable: true })
        setModalParametro({ isOpen: false, tipo: 'crear' })
        showToast({
          type: 'success',
          title: 'Parámetro actualizado',
          message: 'El parámetro personalizado se actualizó correctamente.',
        })
      } catch (error) {
        console.error('Error al actualizar parámetro personalizado:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible actualizar el parámetro personalizado.',
          titulosPorCodigo: {
            validation_error: 'Datos inválidos',
            forbidden: 'Acceso denegado',
            not_found: 'Parámetro no encontrado',
            conflict: 'Parámetro duplicado',
          },
          mensajesPorCodigo: {
            validation_error: 'Verifica la información capturada antes de guardar los cambios.',
            forbidden: 'No tienes permisos para editar parámetros personalizados.',
            not_found: 'El parámetro que intentas editar ya no existe.',
          },
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
      }
    }
  }

  const eliminarParametroPersonalizado = async (id: string) => {
    try {
      await configuracionService.eliminarParametroPersonalizado(id)
      setParametrosPersonalizados(prev => prev.filter(p => p.id !== id))
      showToast({
        type: 'success',
        title: 'Parámetro eliminado',
        message: 'El parámetro personalizado se eliminó correctamente.',
      })
    } catch (error) {
      console.error('Error al eliminar parámetro personalizado:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible eliminar el parámetro personalizado.',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Parámetro no encontrado',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar parámetros personalizados.',
          not_found: 'El parámetro que intentas eliminar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const secciones = [
    { id: 'general', nombre: 'General', icono: <CogIcon className="h-5 w-5" /> },
    { id: 'seguridad', nombre: 'Seguridad', icono: <ShieldCheckIcon className="h-5 w-5" /> },
    { id: 'email', nombre: 'Email', icono: <EnvelopeIcon className="h-5 w-5" /> },
    { id: 'almacenamiento', nombre: 'Almacenamiento', icono: <CircleStackIcon className="h-5 w-5" /> },
    { id: 'notificaciones', nombre: 'Notificaciones', icono: <BellIcon className="h-5 w-5" /> },
    { id: 'personalizado', nombre: 'Personalizado', icono: <KeyIcon className="h-5 w-5" /> }
  ]

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando configuración del sistema...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración del Sistema</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Gestiona los parámetros y configuraciones administrativas del sistema
              </p>
            </div>
            {tienePermiso('configuracion:editar') && (
              <button
                onClick={() => guardarConfiguracion(seccionActiva)}
                disabled={guardando}
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors duration-200"
              >
                {guardando ? (
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                )}
                {guardando ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar de navegación */}
          <div className="lg:w-64">
            <nav className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <ul className="space-y-2">
                {secciones.map((seccionItem) => (
                  <li key={seccionItem.id}>
                    <button
                      onClick={() => navigate(seccionItem.id === 'general' ? '/admin/configuracion' : `/admin/configuracion/${seccionItem.id}`)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors duration-200 ${
                        seccionActiva === seccionItem.id
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {seccionItem.icono}
                      <span className="font-medium">{seccionItem.nombre}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Contenido principal */}
          <div className="flex-1">
            <AnimatePresence mode="wait">
              {seccionActiva === 'general' && (
                <motion.div
                  key="general"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Configuración General
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="admin-config-nombre-sistema" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nombre del Sistema
                      </label>
                      <input
                        id="admin-config-nombre-sistema"
                        name="configuracionGeneral.nombreSistema"
                        type="text"
                        value={configuracionGeneral.nombreSistema}
                        onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, nombreSistema: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-idioma-defecto" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Idioma Predeterminado
                      </label>
                      <select
                        id="admin-config-idioma-defecto"
                        name="configuracionGeneral.idiomaDefecto"
                        value={configuracionGeneral.idiomaDefecto}
                        onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, idiomaDefecto: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                        <option value="pt">Português</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="admin-config-descripcion-sistema" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Descripción del Sistema
                      </label>
                      <textarea
                        id="admin-config-descripcion-sistema"
                        name="configuracionGeneral.descripcionSistema"
                        value={configuracionGeneral.descripcionSistema}
                        onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, descripcionSistema: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-zona-horaria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Zona Horaria
                      </label>
                      <select
                        id="admin-config-zona-horaria"
                        name="configuracionGeneral.zonaHoraria"
                        value={configuracionGeneral.zonaHoraria}
                        onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, zonaHoraria: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
                        <option value="America/New_York">Nueva York (GMT-5)</option>
                        <option value="Europe/Madrid">Madrid (GMT+1)</option>
                        <option value="Asia/Tokyo">Tokio (GMT+9)</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="admin-config-formato-fecha" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Formato de Fecha
                      </label>
                      <select
                        id="admin-config-formato-fecha"
                        name="configuracionGeneral.formatoFecha"
                        value={configuracionGeneral.formatoFecha}
                        onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, formatoFecha: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <input
                            id="admin-config-modo-mantenimiento"
                            name="configuracionGeneral.modoMantenimiento"
                            type="checkbox"
                            checked={configuracionGeneral.modoMantenimiento}
                            onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, modoMantenimiento: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-modo-mantenimiento" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Modo de mantenimiento activo
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="admin-config-registro-publico"
                            name="configuracionGeneral.registroPublico"
                            type="checkbox"
                            checked={configuracionGeneral.registroPublico}
                            onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, registroPublico: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-registro-publico" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Permitir registro público de usuarios
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="admin-config-verificacion-email"
                            name="configuracionGeneral.verificacionEmail"
                            type="checkbox"
                            checked={configuracionGeneral.verificacionEmail}
                            onChange={(e) => setConfiguracionGeneral(prev => ({ ...prev, verificacionEmail: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-verificacion-email" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Requerir verificación de email
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {seccionActiva === 'seguridad' && (
                <motion.div
                  key="seguridad"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Configuración de Seguridad
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="admin-config-longitud-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Longitud Mínima de Contraseña
                      </label>
                      <input
                        id="admin-config-longitud-password"
                        name="configuracionSeguridad.longitudMinimaPassword"
                        type="number"
                        min="6"
                        max="32"
                        value={configuracionSeguridad.longitudMinimaPassword}
                        onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, longitudMinimaPassword: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-expiracion-sesion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tiempo de Expiración de Sesión (horas)
                      </label>
                      <input
                        id="admin-config-expiracion-sesion"
                        name="configuracionSeguridad.expiracionSesion"
                        type="number"
                        min="1"
                        max="168"
                        value={configuracionSeguridad.expiracionSesion}
                        onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, expiracionSesion: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-intentos-login" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Intentos Máximos de Login
                      </label>
                      <input
                        id="admin-config-intentos-login"
                        name="configuracionSeguridad.intentosMaximosLogin"
                        type="number"
                        min="3"
                        max="10"
                        value={configuracionSeguridad.intentosMaximosLogin}
                        onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, intentosMaximosLogin: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-tiempo-bloqueo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tiempo de Bloqueo (minutos)
                      </label>
                      <input
                        id="admin-config-tiempo-bloqueo"
                        name="configuracionSeguridad.tiempoBloqueo"
                        type="number"
                        min="5"
                        max="60"
                        value={configuracionSeguridad.tiempoBloqueo}
                        onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, tiempoBloqueo: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <input
                            id="admin-config-caracteres-especiales"
                            name="configuracionSeguridad.requiereCaracteresEspeciales"
                            type="checkbox"
                            checked={configuracionSeguridad.requiereCaracteresEspeciales}
                            onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, requiereCaracteresEspeciales: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-caracteres-especiales" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Requerir caracteres especiales en contraseñas
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="admin-config-requiere-numeros"
                            name="configuracionSeguridad.requiereNumeros"
                            type="checkbox"
                            checked={configuracionSeguridad.requiereNumeros}
                            onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, requiereNumeros: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-requiere-numeros" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Requerir números en contraseñas
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="admin-config-requiere-mayusculas"
                            name="configuracionSeguridad.requiereMayusculas"
                            type="checkbox"
                            checked={configuracionSeguridad.requiereMayusculas}
                            onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, requiereMayusculas: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-requiere-mayusculas" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Requerir mayúsculas en contraseñas
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="admin-config-dos-factores"
                            name="configuracionSeguridad.autenticacionDosFactor"
                            type="checkbox"
                            checked={configuracionSeguridad.autenticacionDosFactor}
                            onChange={(e) => setConfiguracionSeguridad(prev => ({ ...prev, autenticacionDosFactor: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-config-dos-factores" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Habilitar autenticación de dos factores
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {seccionActiva === 'email' && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Configuración de Email
                    </h2>
                    <button
                      onClick={probarConexionEmail}
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Probar Conexión
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="admin-config-servidor-smtp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Servidor SMTP
                      </label>
                      <input
                        id="admin-config-servidor-smtp"
                        name="configuracionEmail.servidorSMTP"
                        type="text"
                        value={configuracionEmail.servidorSMTP}
                        onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, servidorSMTP: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-puerto-smtp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Puerto SMTP
                      </label>
                      <input
                        id="admin-config-puerto-smtp"
                        name="configuracionEmail.puertoSMTP"
                        type="number"
                        value={configuracionEmail.puertoSMTP}
                        onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, puertoSMTP: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-usuario-smtp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Usuario SMTP
                      </label>
                      <input
                        id="admin-config-usuario-smtp"
                        name="configuracionEmail.usuarioSMTP"
                        type="email"
                        value={configuracionEmail.usuarioSMTP}
                        onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, usuarioSMTP: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-config-password-smtp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Contraseña SMTP
                      </label>
                      <div className="relative">
                        <input
                          id="admin-config-password-smtp"
                          name="configuracionEmail.passwordSMTP"
                          type={mostrarPassword ? 'text' : 'password'}
                          value={configuracionEmail.passwordSMTP}
                          onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, passwordSMTP: e.target.value }))}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarPassword(!mostrarPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {mostrarPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="admin-config-encriptacion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Encriptación
                      </label>
                      <select
                        id="admin-config-encriptacion"
                        name="configuracionEmail.encriptacion"
                        value={configuracionEmail.encriptacion}
                        onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, encriptacion: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="none">Sin encriptación</option>
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="admin-config-email-remitente" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Remitente
                      </label>
                      <input
                        id="admin-config-email-remitente"
                        name="configuracionEmail.emailRemitente"
                        type="email"
                        value={configuracionEmail.emailRemitente}
                        onChange={(e) => setConfiguracionEmail(prev => ({ ...prev, emailRemitente: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {seccionActiva === 'personalizado' && (
                <motion.div
                  key="personalizado"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Parámetros Personalizados
                    </h2>
                    {tienePermiso('configuracion:editar') && (
                      <button
                        onClick={() => setModalParametro({ isOpen: true, tipo: 'crear' })}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Nuevo Parámetro
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Clave
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Valor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Categoría
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {parametrosPersonalizados.map((parametro) => (
                          <tr key={parametro.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {parametro.clave}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {parametro.valor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                parametro.tipo === 'number' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                parametro.tipo === 'boolean' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                parametro.tipo === 'json' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                              }`}>
                                {parametro.tipo}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {parametro.categoria}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => editarParametroPersonalizado(parametro)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => eliminarParametroPersonalizado(parametro.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Modal para parámetros personalizados */}
      <AnimatePresence>
        {modalParametro.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {modalParametro.tipo === 'crear' ? 'Nuevo Parámetro' : 'Editar Parámetro'}
                </h3>
                <button
                  onClick={() => setModalParametro({ isOpen: false, tipo: 'crear' })}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="admin-config-parametro-clave" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Clave
                  </label>
                  <input
                    id="admin-config-parametro-clave"
                    name="nuevoParametro.clave"
                    type="text"
                    value={nuevoParametro.clave || ''}
                    onChange={(e) => setNuevoParametro(prev => ({ ...prev, clave: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-config-parametro-valor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor
                  </label>
                  <input
                    id="admin-config-parametro-valor"
                    name="nuevoParametro.valor"
                    type="text"
                    value={nuevoParametro.valor || ''}
                    onChange={(e) => setNuevoParametro(prev => ({ ...prev, valor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-config-parametro-tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo
                  </label>
                  <select
                    id="admin-config-parametro-tipo"
                    name="nuevoParametro.tipo"
                    value={nuevoParametro.tipo || 'string'}
                    onChange={(e) => setNuevoParametro(prev => ({ ...prev, tipo: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="string">Texto</option>
                    <option value="number">Número</option>
                    <option value="boolean">Booleano</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-config-parametro-categoria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoría
                  </label>
                  <input
                    id="admin-config-parametro-categoria"
                    name="nuevoParametro.categoria"
                    type="text"
                    value={nuevoParametro.categoria || ''}
                    onChange={(e) => setNuevoParametro(prev => ({ ...prev, categoria: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-config-parametro-descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descripción
                  </label>
                  <textarea
                    id="admin-config-parametro-descripcion"
                    name="nuevoParametro.descripcion"
                    value={nuevoParametro.descripcion || ''}
                    onChange={(e) => setNuevoParametro(prev => ({ ...prev, descripcion: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setModalParametro({ isOpen: false, tipo: 'crear' })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={modalParametro.tipo === 'crear' ? agregarParametroPersonalizado : guardarParametroEditado}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  {modalParametro.tipo === 'crear' ? 'Crear' : 'Guardar'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default PaginaConfiguracionSistema
